import {createUserManager} from '@competencegroup/redux-oidc-es6';
import OidcClient from 'oidc-client/index.js';
import {leerplatformSettingsStore} from '../stores/leerplatformSettingsStore';
import type {EnvironmentConfig} from '../stores/leerplatformSettingsStore';

// Define TypeScript interfaces
interface UserManagerConfig {
    client_id: string;
    redirect_uri: string;
    post_logout_redirect_uri: string;
    response_type: string;
    scope: string;
    authority: string;
    silent_redirect_uri: string;
    automaticSilentRenew: boolean;
    filterProtocolClaims: boolean;
    loadUserInfo: boolean;
    userStore: OidcClient.WebStorageStateStore;
}

let environmentConfig: EnvironmentConfig | null = null;

async function fetchEnvironmentConfig(): Promise<EnvironmentConfig> {
    if (environmentConfig) {
        return environmentConfig;
    }

    try {
        const config = await leerplatformSettingsStore.loadEnvironmentConfig();
        environmentConfig = config;
        return config;
    } catch (error) {
        console.error('Error fetching environment config:', error);
        // Return a default config as fallback
        return {
            settingsUrl: '',
            hlUrl: '',
            skillsApiUrl: '',
            catalogApiUrl: '',
            accreditationApiUrl: '',
            messagesApiUrl: '',
            ssoApiUrl: '',
            domSecManagementApiUrl: '',
            accessManagementApiUrl: '',
            academyDepartmentsUrl: '',
            planningIntegrationApiUrl: '',
            reportApiUrl: '',
            importApiUrl: '',
            notificationsApiUrl: '',
            apiUrl: '',
            authority: 'https://sts-dev.competence.biz/identity',
        };
    }
}

export function getClientId(): string {
    switch (window.location.hostname) {
        case 'localhost':
        case 'develop.test-sdbleerplatform.nl':
            return 'https://develop5.tcg-academy.nl';
        default:
            return `https://${window.location.hostname}`;
    }
}

export function getAuthoritySync(): string {
    if (environmentConfig && environmentConfig.authority) {
        return environmentConfig.authority;
    }

    // Fallback to original logic until environment is loaded
    return [
        'localhost',
        'develop.tcg-minerva.nl',
        'test.tcg-minerva.nl',
        'develop.test-sdbleerplatform.nl',
        'test.test-sdbleerplatform.nl',
    ].indexOf(window.location.hostname.toLowerCase()) === -1
        ? 'https://sts.competence.biz/identity'
        : 'https://sts-dev.competence.biz/identity';
}

// Start fetching the environment config immediately
const environmentPromise = fetchEnvironmentConfig();

function createUserManagerConfig(authority: string): UserManagerConfig {
    return {
        client_id: getClientId(),
        redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/callback`,
        post_logout_redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/`,
        response_type: 'token id_token',
        scope: 'openid name api api_tcg_claims idString email',
        authority: authority,
        silent_redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/silent_renew`,
        automaticSilentRenew: true,
        filterProtocolClaims: false,
        loadUserInfo: false,
        userStore: new OidcClient.WebStorageStateStore({
            store: window.sessionStorage,
        }),
    };
}

// Create a user manager with the authority from environment.json or fallback
const userManager = createUserManager(createUserManagerConfig(getAuthoritySync()));

// Export the async function for components that need to wait for the real authority
export async function getAuthority(): Promise<string> {
    const config = await environmentPromise;
    return config.authority;
}

export default userManager;
